import { router } from "@inertiajs/react"
import { Plus, X } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

/**
 * OKS-26 — Tags editor for the creator detail page (UX Spec V2 §Tags
 * card). Port-adapted from OKS-18 `<CandidateTagsEditor>`; differences:
 *
 *   - Wire form-namespace is **`membership[tags][]`** (NOT
 *     `candidate[tags][]`). BE strong-params:
 *     `params.require(:membership).permit(:notes, :engagement_rate, tags: [])`
 *   - PATCH target is **BE-supplied `actions.update_url`**.
 *   - Suggestions sourced from `brand_tags` prop (BE pulls from
 *     `Candidates::FilterOptionsDigest.tags` — same brand vocabulary
 *     shared with the candidate-detail typeahead).
 *   - Errors keyed `errors["membership.tags"]` per BE rev-5.
 *
 * **Optimistic chip UX with rollback** — UX V2 §Micro-interactions.
 * `router.patch` is imperative (not Inertia `<Form>`) because the
 * optimistic-rollback flow needs imperative control of the request
 * lifecycle (Form's render-prop model can't easily serve "patch
 * invisible to dom + revert on error"). Crew form rules still
 * respected — no shadcn Form / no RHF / no zod.
 *
 * Read-only mode (`!can_edit`): chips render without remove
 * affordance; no input.
 *
 * Locked testids:
 *   - `creator-detail-tags-section` — Card wrapper
 *   - `creator-detail-tags-form` — `<form>` element (around add input)
 *   - `creator-detail-tags-input` — Combobox trigger button
 *   - `creator-detail-tags-submit` — Add / Save button (sr-only fallback)
 *   - `creator-detail-tag-chip` — each tag chip
 *   - `creator-detail-tag-remove` — X on each chip
 */
interface CrewMemberTagsEditorProps {
  /** Server-side tags array. */
  tags: string[]
  /** Brand-scoped tag suggestions. Same source as candidate-detail. */
  brandTags: string[]
  /** BE-supplied PATCH target — `actions.update_url`. */
  updateUrl: string
  canEdit: boolean
}

export function CrewMemberTagsEditor({
  tags: serverTags,
  brandTags,
  updateUrl,
  canEdit,
}: CrewMemberTagsEditorProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [optimistic, setOptimistic] = useState<string[] | null>(null)
  const [pendingInput, setPendingInput] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const tags = optimistic ?? serverTags
  const trimmedInput = pendingInput.trim()
  const canAdd =
    canEdit &&
    online &&
    !submitting &&
    trimmedInput !== "" &&
    !tags.includes(trimmedInput)

  // Suggestions = brandTags MINUS already-applied tags. Free-text typing
  // works alongside via CommandEmpty's "Add: {{query}}" affordance.
  const suggestions = useMemo(
    () => brandTags.filter((tag) => !tags.includes(tag)),
    [brandTags, tags],
  )

  function persist(next: string[]) {
    setSubmitting(true)
    // **Wire-format canary** — nested under `membership` namespace for
    // BE's strong-params surface. NEVER top-level `tags` (would 400).
    router.patch(
      updateUrl,
      { membership: { tags: next } },
      {
        preserveScroll: true,
        onError: () => {
          setOptimistic(null) // rollback
          toast.error(t("crew.detail.toasts.tags_save_failed"))
        },
        onSuccess: () => {
          setOptimistic(null) // server tags become source of truth
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function add(rawTag?: string) {
    const tag = (rawTag ?? trimmedInput).trim().toLowerCase()
    if (!canEdit || !online || submitting) return
    if (tag === "" || tags.includes(tag)) return
    const next = [...tags, tag]
    setOptimistic(next)
    setPendingInput("")
    setPopoverOpen(false)
    persist(next)
  }

  function remove(tag: string) {
    if (!canEdit || submitting) return
    const next = tags.filter((existing) => existing !== tag)
    setOptimistic(next)
    persist(next)
  }

  return (
    <Card data-testid="creator-detail-tags-section">
      <CardHeader>
        <CardTitle>{t("crew.detail.tags.section_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "flex flex-wrap gap-2",
            tags.length === 0 && "text-muted-foreground text-sm",
          )}
        >
          {tags.length === 0 && (
            <span>{t("crew.detail.tags.no_tags_yet")}</span>
          )}
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              data-testid="creator-detail-tag-chip"
              data-tag={tag}
              className={cn("gap-1.5", canEdit && "pr-1")}
            >
              <span>{tag}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove(tag)}
                  aria-label={t("crew.detail.tags.remove_tag_aria", { tag })}
                  data-testid="creator-detail-tag-remove"
                  data-tag={tag}
                  disabled={submitting}
                  className="hover:bg-muted -mr-0.5 rounded-sm p-0.5 disabled:opacity-50"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        {canEdit && (
          <form
            data-testid="creator-detail-tags-form"
            onSubmit={(e) => {
              e.preventDefault()
              add()
            }}
            className="mt-4"
          >
            <Label htmlFor="creator-tags-input" className="sr-only">
              {t("crew.detail.tags.add_tag")}
            </Label>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    id="creator-tags-input"
                    data-testid="creator-detail-tags-input"
                    disabled={submitting || !online}
                    className="w-full justify-start"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("crew.detail.tags.add_tag")}
                  </Button>
                }
              />
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t("crew.detail.tags.add_tag")}
                    value={pendingInput}
                    onValueChange={setPendingInput}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {trimmedInput !== "" && !tags.includes(trimmedInput) ? (
                        <button
                          type="button"
                          className="hover:bg-accent w-full px-2 py-1.5 text-left text-sm"
                          onClick={() => add()}
                        >
                          {t("crew.detail.tags.add_typed", {
                            query: trimmedInput,
                          })}
                        </button>
                      ) : (
                        <span className="text-muted-foreground px-2 py-1.5 text-sm">
                          {t("crew.detail.tags.empty_suggestions")}
                        </span>
                      )}
                    </CommandEmpty>
                    {suggestions.length > 0 && (
                      <CommandGroup
                        heading={t("crew.detail.tags.existing_label")}
                      >
                        {suggestions.map((tag) => (
                          <CommandItem
                            key={tag}
                            value={tag}
                            onSelect={() => add(tag)}
                            data-tag={tag}
                          >
                            {tag}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {/*
              Sr-only `<input name="membership[tags][]">` payload would
              be redundant here — saves go via imperative `router.patch`
              which serializes the next array directly. We expose a
              hidden submit so Enter inside CommandInput triggers add.
            */}
            <button
              type="submit"
              data-testid="creator-detail-tags-submit"
              disabled={!canAdd}
              className="sr-only"
            >
              {t("crew.detail.tags.save")}
            </button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
